import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useUserRegion } from '@frontend/components/agromarket/components/RegionSelector'
import { FundingProduct } from '@frontend/components/agromarket/core/access-control'
import { EColor } from '@frontend/pole-ui/lib/common/constants'
import { ActionIcon } from '@frontend/pole-ui/lib/components/Icon/svg/ActionIcon'
import { IncreaseLoanAmountBannerLight } from '@/components/banners/IncreaseLoanAmountBannerLight'
import { FINANCING_LANDING_ROOT_BREADCRUMBS } from '@/components/composed/landingBlocks/constants'
import { LandingAccessControlProvider } from '@/components/composed/landingBlocks/LandingAccessControlProvider'
import { LandingApplyingOfferFormNew } from '@/components/composed/landingBlocks/LandingApplyingOfferFormNew'
import { LandingFAQ } from '@/components/composed/landingBlocks/LandingFAQ'
import { LandingHeader } from '@/components/composed/landingBlocks/LandingHeader'
import { openModal } from '@/components/composed/Modal/actions'
import { addModalToWellKnown } from '@/components/composed/Modal/constants'
import { VideoModal } from '@/components/composed/modals'
import { Button } from '@/components/ui'
import { formatHighlightedText } from '@/components/ui/HighlightedText'
import { getLocation } from '@/logic/app/reducer'
import { financingProduct } from '@/logic/metrika/constants'
import {
  getEventContextGtmFinancingOfferLendingShow,
  getGtmScrollEventPayloadFinancingOfferForm,
  pushGtmEventOnClickApplyOnFundingOfferPage,
  pushGtmEventOnShowLendingFinancingProducts,
} from '@/logic/metrika/financing/gtmHelpers'
import type { IBaseGtmEventFinancing } from '@/logic/metrika/financing/types'
import { getUserGtmInfo } from '@/logic/metrika/helpers'
import { usePushScrollGtmEvent } from '@/logic/metrika/usePushScrollGtmEvent'
import { pushGtmEventOnClickFinancingActionOffer } from '@/pages/AdvancePaymentOnFutureHarvest/gtm-helpers'
import { ApplyingFinancingOfferSuccessModal } from '@/pages/Financing/components'
import { FinancingProductHelper } from '@/pages/Financing/components/FinancingProductHelper'
import { NAV_SUCCESS_NEW_ITEMS } from '@/pages/FinancingAgent/constants'
import type { RootState } from '@/redux/interfaces'
import type { EFinancingOfferRoute } from '@/types'
import { EFinancingOfferType } from '@/types'
import { asyncScrollIntoView } from '@/utils/helpers'
import { useWebpExtension } from '@/utils/hooks/image'
import { getStaticImagePath } from '@/utils/images'
import { LandingDetails } from '../../components/composed/landingBlocks/LandingDetails'
import { LandingSteps } from '../../components/composed/landingBlocks/LandingSteps'
import { AdvancePaymentCalculator } from './components/AdvancePaymentCalculator'
import { LandingBenefits } from './components/LandingBenefits'
import { LandingLoanDiscount } from './components/LandingLoanDiscount'
import { LandingLoanTerms } from './components/LandingLoanTerms'
import {
  BENEFITS,
  BREADCRUMB,
  ECommonLandingAnchor,
  FAQ,
  FAQ_FORMAT_OPTIONS,
  LANDING_DETAILS,
  LINK_TO_LANDING_VIDEO,
  STEPS,
  TERM_GROUPS,
} from './constants'
import { EAdvancePaymentOnFutureHarvestScrollEventBlockId } from './gtmContants'
import { messages } from './messages'
import type { TAdvancePaymentOnFutureHarvestProps } from './types'
import './styles.scss'

addModalToWellKnown({ VideoModal, ApplyingFinancingOfferSuccessModal })

const mapState = (state: RootState) => ({
  location: getLocation()(state),
  userGtmInfo: getUserGtmInfo()(state),
})

const AdvancePaymentOnFutureHarvest: React.FC<TAdvancePaymentOnFutureHarvestProps> = () => {
  const { location, userGtmInfo } = useSelector(mapState)

  const imageExtension = useWebpExtension()
  const dispatch = useDispatch()
  const { userRegion } = useUserRegion()

  const handleClickSubmitApplication = (positionClick?: string) => {
    asyncScrollIntoView(`#${ECommonLandingAnchor.landingApplyingOfferForm}`)
    pushGtmEventOnClickApplyOnFundingOfferPage(userGtmInfo, userRegion.name, FundingProduct.ADVANCE_FUNDING)
    pushGtmEventOnClickFinancingActionOffer(userGtmInfo, userRegion.name, `request-${positionClick}`)
  }

  const handleClickWatchVideo = () => {
    dispatch(openModal({ options: { dialogId: 'VideoModal' }, contentOptions: { videoUrl: LINK_TO_LANDING_VIDEO } }))
    pushGtmEventOnClickFinancingActionOffer(userGtmInfo, userRegion.name, 'avans-head')
  }

  useEffect(() => {
    pushGtmEventOnShowLendingFinancingProducts(
      userGtmInfo,
      getEventContextGtmFinancingOfferLendingShow(location.pathname, financingProduct[location.pathname as EFinancingOfferRoute]),
    ) // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  usePushScrollGtmEvent<EAdvancePaymentOnFutureHarvestScrollEventBlockId, IBaseGtmEventFinancing>({
    blockIds: [EAdvancePaymentOnFutureHarvestScrollEventBlockId.financingOfferForm],
    gtmScrollEventPayloads: {
      [EAdvancePaymentOnFutureHarvestScrollEventBlockId.financingOfferForm]: getGtmScrollEventPayloadFinancingOfferForm(
        userGtmInfo,
        userRegion.name,
        FundingProduct.ADVANCE_FUNDING,
      ),
    },
  })

  const stepsImage = getStaticImagePath(`financingOffer/advancePaymentOnFutureHarvest/suitcase.${imageExtension}`)
  const headerIllustration = getStaticImagePath(`financingOffer/advancePaymentOnFutureHarvest/harvester.${imageExtension}`)

  return (
    <LandingAccessControlProvider>
      <div className="advancePaymentOnFutureHarvestPage">
        <LandingHeader
          currentPageBreadcrumb={BREADCRUMB}
          rootBreadcrumbs={FINANCING_LANDING_ROOT_BREADCRUMBS}
          title={formatHighlightedText(messages.header.title)}
          pageTitle={messages.breadcrumbs.currentPage}
          description={messages.header.description}
          additionalButton={
            <Button
              className="advancePaymentOnFutureHarvestPage__watchVideo"
              modifiers={['outline', 'full-width-mobile']}
              iconProps={{
                component: <ActionIcon />,
                style: {
                  color: EColor.GREEN,
                },
              }}
              onClick={handleClickWatchVideo}
            >
              {messages.header.additionalButton}
            </Button>
          }
          image={headerIllustration}
          onClickSubmitApplication={() => handleClickSubmitApplication('head')}
          withAnimation
        />
        <LandingDetails details={LANDING_DETAILS} />
        <AdvancePaymentCalculator onClickSubmitApplication={() => handleClickSubmitApplication('calculator')} />
        <LandingLoanDiscount className="hidden" onClickGetDiscountButton={() => handleClickSubmitApplication('stages')} />
        <LandingSteps
          title={formatHighlightedText(messages.steps.title)}
          image={stepsImage}
          steps={STEPS}
          submitButtonText={messages.steps.button}
          onClickSubmitButton={() => handleClickSubmitApplication('stages')}
        />
        <LandingBenefits title={formatHighlightedText(messages.benefits.title)} benefits={BENEFITS} />
        <LandingLoanTerms title={formatHighlightedText(messages.loanTerms.title)} termGroups={TERM_GROUPS} />
        <LandingFAQ
          title={formatHighlightedText(messages.faq.title)}
          items={FAQ}
          itemsValues={FAQ_FORMAT_OPTIONS}
          blockNameGtm="blockFinanceAvans"
        />
        <LandingApplyingOfferFormNew
          title={formatHighlightedText(messages.form.title)}
          description={formatHighlightedText(messages.form.description)}
          offerType={EFinancingOfferType.ADVANCE_FUNDING}
          productId={FundingProduct.ADVANCE_FUNDING}
          formId={ECommonLandingAnchor.landingApplyingOfferForm}
          withAnimation
          banner={
            <IncreaseLoanAmountBannerLight
              className="advancePaymentOnFutureHarvestPage__increaseLoanAmountBanner"
              buttonModifiers={['outline-gray', 'outline-small']}
              comment={messages.increaseLoanAmountBanner.comment}
            />
          }
          navItems={NAV_SUCCESS_NEW_ITEMS}
          isShowUxFeedbackModal
        />
      </div>
      <FinancingProductHelper />
    </LandingAccessControlProvider>
  )
}

export default AdvancePaymentOnFutureHarvest
